// src/pages/Lageplan.jsx
//
// Pin-Editor fuer den Lageplan: das Planbild anzeigen, eine Station auswaehlen,
// auf den Plan klicken – fertig. Gespeichert werden PROZENTWERTE der Bildbreite
// bzw. -hoehe (`map_x` / `map_y` am Stationsdokument), nicht Pixel. Dadurch
// bleiben gesetzte Pins gueltig, wenn der Plan spaeter in anderer Aufloesung
// ausgetauscht wird, und dieselben Werte bedienen den A5-Zettel wie einen
// A3-Aushang.
//
// Das Bild kommt bewusst aus dem Backend (GET /public/lageplan) und nicht aus
// dem `public/`-Ordner des Frontends: die PDF-Erzeugung liest dieselbe Datei.
// Zwei Kopien wuerden beim naechsten Planwechsel auseinanderlaufen, und die
// Pins saessen im Druck woanders als hier im Editor.
//
// Neben den Stationen lassen sich EIGENE ORTE setzen ("Anmeldung",
// "Getränkeverkauf", …): Dinge, die kein Spiel sind, aber gefunden werden
// wollen. Sie tragen Buchstaben statt Zahlen (A, B, …) und stehen im Druck als
// blaue Quadrate unter dem Plan erklärt. Der Buchstabe ergibt sich aus der
// Reihenfolge der Liste – genau wie im Backend (services/lageplanOrte.js).
//
// Neben der Position wird hier auch der BEREICH gepflegt (Halle, Parkplatz
// Nord, …). Beides gehoert in denselben Arbeitsschritt, weil man beim
// Einrichten des Plans ohnehin in Flaechen denkt – und weil der Bereich
// traegt, was ein Luftbild nicht zeigen kann: von der Halle ist darauf nur das
// Dach zu sehen.
import { useCallback, useEffect, useRef, useState } from "react";
import { MapPin, Save, Trash2, RefreshCw, Plus } from "lucide-react";
import { mitSitzungspruefung } from "../utils/fetchWithAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Gruppen-Ableitung wie im Backend (allow_groups neu, allow_kids alt). */
function gruppe(station) {
  return station.allow_groups ?? (station.allow_kids === false ? "Erwachsene" : "Alle");
}

// Pin-Groessen: dieselben Schluessel wie PIN_GROESSEN in functions/pdf/lageplan.js,
// damit die Vorschau hier exakt zeigt, was spaeter auf dem Zettel steht.
// Tailwind-Klassen statt Inline-Styles fuer die Kreisgroesse, weil die Groesse
// gleichzeitig fuer Pin UND Zahl innerhalb passen muss.
const PIN_GROESSEN = {
  klein: { kreis: "w-5 h-5", text: "text-[9px]" },
  mittel: { kreis: "w-7 h-7", text: "text-[11px]" },
  gross: { kreis: "w-9 h-9", text: "text-sm" },
};
const PIN_GROESSE_STANDARD = "mittel";

function pinGroesse(station) {
  return PIN_GROESSEN[station.map_size] || PIN_GROESSEN[PIN_GROESSE_STANDARD];
}

// Dieselbe Obergrenze und Namenslänge wie im Backend (services/lageplanOrte.js).
const MAX_ORTE = 20;
const MAX_ORT_NAME = 30;

const buchstabe = (index) => String.fromCharCode(65 + index);

let ortZaehler = 0;
// Nur für React-Keys und die Auswahl im Editor – wird nicht gespeichert.
const neueOrtId = () => `ort-${Date.now()}-${++ortZaehler}`;

function hatPosition(station) {
  // Number(null) waere 0 – ein rechnerisch gueltiger Wert. Ohne den expliziten
  // null/undefined-Check zaehlt eine noch nicht gesetzte Station als Pin bei
  // (0, 0): sie wuerde links oben angezeigt und beim PDF-Export einen Crash
  // ausloesen (siehe pdf/lageplan.js).
  if (station.map_x == null || station.map_y == null) return false;
  return Number.isFinite(Number(station.map_x)) && Number.isFinite(Number(station.map_y));
}

export default function Lageplan() {
  const token = localStorage.getItem("token");

  const [stationen, setStationen] = useState([]);
  const [ladend, setLadend] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [planFehlt, setPlanFehlt] = useState(false);
  const [aktiv, setAktiv] = useState(null); // station_number
  const [speichernd, setSpeichernd] = useState(false);
  const [hinweis, setHinweis] = useState(null);
  const [geaendert, setGeaendert] = useState(false);

  // Eigene Orte. `orteGeladen` verhindert, dass ein fehlgeschlagener Abruf beim
  // Speichern als "keine Orte" gewertet wird und die gespeicherten löscht.
  const [orte, setOrte] = useState([]);
  const [orteGeladen, setOrteGeladen] = useState(false);
  const [aktivOrt, setAktivOrt] = useState(null); // id
  const [neuerOrt, setNeuerOrt] = useState("");

  const bildRef = useRef(null);

  // === Stationen laden ===
  const laden = useCallback(async () => {
    setLadend(true);
    setFehler(null);
    try {
      const kopf = { headers: { Authorization: `Bearer ${token}` } };
      const [res, orteRes] = await Promise.all([
        fetch(`${BACKEND_URL}/stations`, kopf),
        fetch(`${BACKEND_URL}/stations/lageplan`, kopf).catch(() => null),
      ]);
      mitSitzungspruefung(res);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const daten = await res.json();

      // Die Orte sind ein Zusatz: scheitert nur ihr Abruf, bleibt der Editor für
      // die Stationen benutzbar, und gespeichert wird dann ohne Orte.
      let geladeneOrte = null;
      if (orteRes && orteRes.ok) {
        const o = await orteRes.json();
        geladeneOrte = (o.orte || []).map((ort) => ({ ...ort, id: neueOrtId() }));
      }
      setOrte(geladeneOrte || []);
      setOrteGeladen(geladeneOrte !== null);
      setAktivOrt(null);
      const sortiert = [...daten].sort(
        (a, b) => Number(a.station_number || 0) - Number(b.station_number || 0)
      );
      setStationen(sortiert);
      setGeaendert(false);
      // Die erste noch nicht gesetzte Station vorwaehlen: beim Einrichten
      // arbeitet man die Liste ohnehin von oben nach unten durch.
      const offen = sortiert.find((s) => !hatPosition(s));
      setAktiv((offen || sortiert[0])?.station_number ?? null);
    } catch (err) {
      console.error("[Lageplan] Stationen konnten nicht geladen werden:", err);
      setFehler("Die Stationen konnten nicht geladen werden.");
    } finally {
      setLadend(false);
    }
  }, [token]);

  useEffect(() => {
    laden();
  }, [laden]);

  // === Klick auf den Plan → Position der aktiven Station ===
  const setzePin = (event) => {
    if ((aktiv == null && aktivOrt == null) || !bildRef.current) return;

    // getBoundingClientRect statt offsetWidth: das Bild wird responsiv
    // skaliert, und nur das Rect kennt die tatsaechlich dargestellte Groesse.
    const rect = bildRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    // Randfaelle abschneiden: ein Klick genau auf die Kante darf keine 100,4 %
    // erzeugen – das Backend wuerde den ganzen Speichervorgang abweisen.
    const begrenzt = (n) => Math.min(100, Math.max(0, Math.round(n * 100) / 100));

    setGeaendert(true);
    setHinweis(null);

    // Ist ein eigener Ort ausgewählt, bekommt er die Position – die Stationen
    // bleiben unberührt.
    if (aktivOrt != null) {
      setOrte((bisher) =>
        bisher.map((o) => (o.id === aktivOrt ? { ...o, map_x: begrenzt(x), map_y: begrenzt(y) } : o))
      );
      return;
    }

    const aktualisiert = stationen.map((s) =>
      s.station_number === aktiv ? { ...s, map_x: begrenzt(x), map_y: begrenzt(y) } : s
    );
    setStationen(aktualisiert);

    // Weiter zur naechsten noch offenen Station – so laesst sich der Plan in
    // einem Durchgang setzen, ohne nach jedem Pin in die Liste zu greifen.
    const naechste = aktualisiert.find((s) => !hatPosition(s));
    if (naechste) setAktiv(naechste.station_number);
  };

  // Station und Ort schließen sich aus: ein Klick auf den Plan setzt immer genau
  // eines von beiden.
  const waehleStation = (nummer) => {
    setAktiv(nummer);
    setAktivOrt(null);
  };

  const waehleOrt = (id) => {
    setAktivOrt(id);
    setAktiv(null);
  };

  const aendereOrt = (id, felder) => {
    setOrte((bisher) => bisher.map((o) => (o.id === id ? { ...o, ...felder } : o)));
    setGeaendert(true);
    setHinweis(null);
  };

  const ortHinzufuegen = (event) => {
    event.preventDefault();
    const name = neuerOrt.trim().slice(0, MAX_ORT_NAME);
    if (!name || orte.length >= MAX_ORTE) return;
    const id = neueOrtId();
    setOrte((bisher) => [
      ...bisher,
      { id, name, map_x: null, map_y: null, map_size: PIN_GROESSE_STANDARD },
    ]);
    setNeuerOrt("");
    setGeaendert(true);
    setHinweis(null);
    // Gleich auswählen: der nächste Klick auf den Plan setzt ihn.
    waehleOrt(id);
  };

  const ortEntfernen = (id) => {
    setOrte((bisher) => bisher.filter((o) => o.id !== id));
    if (aktivOrt === id) setAktivOrt(null);
    setGeaendert(true);
    setHinweis(null);
  };

  const setzeBereich = (nummer, wert) => {
    setStationen((bisher) =>
      bisher.map((s) => (s.station_number === nummer ? { ...s, bereich: wert } : s))
    );
    setGeaendert(true);
    setHinweis(null);
  };

  const setzeGroesse = (nummer, wert) => {
    setStationen((bisher) =>
      bisher.map((s) => (s.station_number === nummer ? { ...s, map_size: wert } : s))
    );
    setGeaendert(true);
    setHinweis(null);
  };

  const entfernePin = (nummer) => {
    setStationen((bisher) =>
      bisher.map((s) => (s.station_number === nummer ? { ...s, map_x: null, map_y: null } : s))
    );
    setGeaendert(true);
    setHinweis(null);
  };

  // === Speichern ===
  //
  // Alle Positionen in EINEM Aufruf. Die Einzelroute PUT /stations/:nr wuerde
  // pro Station eine Ergebnis-Neuberechnung anstossen – fuer eine Aenderung,
  // die mit der Wertung nichts zu tun hat.
  const speichern = async () => {
    setSpeichernd(true);
    setHinweis(null);
    try {
      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/stations/lageplan`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          positionen: stationen.map((s) => ({
            station_number: s.station_number,
            map_x: hatPosition(s) ? s.map_x : null,
            map_y: hatPosition(s) ? s.map_y : null,
            map_size: s.map_size || PIN_GROESSE_STANDARD,
            bereich: (s.bereich || "").trim(),
          })),
          // Nur mitschicken, wenn sie geladen wurden – sonst würde ein Fehler beim
          // Laden die gespeicherten Orte überschreiben.
          ...(orteGeladen && {
            orte: orte.map((o) => ({
              name: o.name,
              map_x: hatPosition(o) ? o.map_x : null,
              map_y: hatPosition(o) ? o.map_y : null,
              map_size: o.map_size || PIN_GROESSE_STANDARD,
            })),
          }),
        }),
      }));
      const daten = await res.json();
      if (!res.ok) throw new Error(daten.error || "Speichern fehlgeschlagen");

      setGeaendert(false);
      // Orte ohne Namen verwirft das Backend – danach dieselbe Liste anzeigen,
      // die tatsächlich gespeichert wurde.
      setOrte((bisher) => bisher.filter((o) => o.name.trim()));
      const ortText = daten.orte ? ` und ${daten.orte} ${daten.orte === 1 ? "Ort" : "Orte"}` : "";
      setHinweis({ art: "ok", text: `${daten.gespeichert} Stationen${ortText} gespeichert.` });
    } catch (err) {
      console.error("[Lageplan] Speichern fehlgeschlagen:", err);
      setHinweis({ art: "fehler", text: err.message || String(err) });
    } finally {
      setSpeichernd(false);
    }
  };

  const gesetzt = stationen.filter(hatPosition).length;
  // Der Buchstabe zählt über die GANZE Liste, auch über Orte ohne Pin – wie im PDF.
  const orteMitBuchstabe = orte.map((o, i) => ({ ...o, buchstabe: buchstabe(i) }));

  // Vorschlagsliste aus dem, was schon vergeben ist. Ohne sie entstehen durch
  // Vertipper Bereiche wie "Parkplatz nord" neben "Parkplatz Nord" – auf dem
  // Zettel waeren das zwei Ueberschriften fuer dieselbe Flaeche.
  const bekannteBereiche = [
    ...new Set(stationen.map((s) => (s.bereich || "").trim()).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b, "de-DE"));

  return (
    <div className="p-6 bg-secondary-950 text-white min-h-screen">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h1 className="text-2xl font-bold flex items-center gap-3">
          <MapPin className="text-yellow-400" />
          Lageplan
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={laden}
            className="px-3 py-2 rounded bg-secondary-800 hover:bg-secondary-700 text-sm flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Neu laden
          </button>
          <button
            onClick={speichern}
            disabled={speichernd || !geaendert}
            className="px-4 py-2 rounded bg-primary-500 hover:bg-primary-600 text-black font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={16} />
            {speichernd ? "Speichert…" : "Positionen speichern"}
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-400 mb-1">
        Station (oder eigenen Ort) links auswählen, dann auf den Plan klicken.
        Danach springt die Auswahl bei Stationen automatisch zur nächsten noch
        offenen.
      </p>
      <p className="text-xs text-gray-500 mb-4">
        {gesetzt} von {stationen.length} Stationen sind auf dem Plan gesetzt. Der
        Bereich pro Station gliedert zusätzlich die Spielliste auf der Vorderseite
        des Team-Zettels – gleiche Schreibweise ergibt eine gemeinsame Überschrift.
        Beides erscheint nach dem Speichern im Export.
      </p>

      {/* Vorschlagsliste, damit Bereiche nicht durch Vertipper aufsplittern. */}
      <datalist id="kirta-bereiche">
        {bekannteBereiche.map((b) => (
          <option key={b} value={b} />
        ))}
      </datalist>

      {fehler && <p className="text-red-400 text-sm mb-4">❌ {fehler}</p>}

      {hinweis && (
        <p
          className={`text-sm mb-4 ${
            hinweis.art === "fehler" ? "text-red-400" : "text-green-400"
          }`}
        >
          {hinweis.art === "fehler" ? "❌ " : "✅ "}
          {hinweis.text}
        </p>
      )}

      {geaendert && (
        <p className="text-amber-400 text-sm mb-4">
          ⚠️ Ungespeicherte Änderungen – erst nach dem Speichern landen sie im PDF.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[18rem_1fr] gap-4">
        <div className="flex flex-col gap-4">
        {/* === Stationsliste === */}
        <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-3 max-h-[70vh] overflow-y-auto">
          {ladend && <p className="text-sm text-gray-400">Stationen werden geladen …</p>}
          {stationen.map((s) => {
            const istAktiv = aktivOrt == null && s.station_number === aktiv;
            return (
              <div
                key={s.station_number}
                className={`flex items-center gap-2 p-2 rounded mb-1 cursor-pointer border ${
                  istAktiv
                    ? "bg-primary-500/15 border-primary-500"
                    : "border-transparent hover:bg-secondary-800"
                }`}
                onClick={() => waehleStation(s.station_number)}
              >
                <span
                  className={`w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
                    hatPosition(s) ? "bg-primary-500 text-black" : "bg-secondary-700 text-gray-400"
                  }`}
                >
                  {s.station_number}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="flex-1 min-w-0 text-sm truncate">
                      {s.station_name || `Station ${s.station_number}`}
                    </span>
                    <span className="text-[10px] text-gray-500 flex-shrink-0">{gruppe(s)}</span>
                  </div>
                  <input
                    type="text"
                    list="kirta-bereiche"
                    maxLength={40}
                    placeholder="Bereich, z. B. Halle"
                    value={s.bereich || ""}
                    onClick={(ev) => ev.stopPropagation()}
                    onChange={(ev) => setzeBereich(s.station_number, ev.target.value)}
                    className="mt-1 w-full px-2 py-1 text-xs rounded bg-secondary-950 border border-secondary-700 text-white placeholder-gray-600 focus:outline-none focus:border-primary-500"
                  />
                  {hatPosition(s) && (
                    <div
                      className="mt-1 flex items-center gap-1"
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <span className="text-[10px] text-gray-500">Pin-Größe:</span>
                      {Object.keys(PIN_GROESSEN).map((groesse) => (
                        <button
                          key={groesse}
                          onClick={() => setzeGroesse(s.station_number, groesse)}
                          className={`px-1.5 py-0.5 rounded text-[10px] capitalize ${
                            (s.map_size || PIN_GROESSE_STANDARD) === groesse
                              ? "bg-primary-500 text-black font-semibold"
                              : "bg-secondary-800 text-gray-400 hover:bg-secondary-700"
                          }`}
                        >
                          {groesse}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {hatPosition(s) && (
                  <button
                    onClick={(ev) => {
                      ev.stopPropagation();
                      entfernePin(s.station_number);
                    }}
                    className="p-1 rounded hover:bg-secondary-700 text-gray-400 self-start"
                    aria-label={`Position von Station ${s.station_number} entfernen`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* === Eigene Orte === */}
        <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-3">
          <h2 className="text-sm font-semibold mb-1">Eigene Orte</h2>
          <p className="text-[11px] text-gray-500 mb-2">
            Alles, was kein Spiel ist, aber gefunden werden soll – z. B. Anmeldung
            oder Getränkeverkauf. Im Druck erscheinen sie mit Buchstaben unter dem Plan.
          </p>

          {!orteGeladen && !ladend && (
            <p className="text-xs text-amber-400 mb-2">
              Die Orte konnten nicht geladen werden – „Neu laden“ versuchen. Bis dahin
              bleiben die gespeicherten Orte beim Speichern unverändert.
            </p>
          )}

          {orteMitBuchstabe.map((o) => {
            const istAktiv = o.id === aktivOrt;
            return (
              <div
                key={o.id}
                className={`flex items-start gap-2 p-2 rounded mb-1 cursor-pointer border ${
                  istAktiv
                    ? "bg-[#2563EB]/15 border-[#2563EB]"
                    : "border-transparent hover:bg-secondary-800"
                }`}
                onClick={() => waehleOrt(o.id)}
              >
                <span
                  className={`w-6 h-6 flex-shrink-0 rounded flex items-center justify-center text-xs font-bold border ${
                    hatPosition(o)
                      ? "bg-[#2563EB] text-white border-white/80"
                      : "bg-secondary-700 text-gray-400 border-transparent"
                  }`}
                >
                  {o.buchstabe}
                </span>
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    maxLength={MAX_ORT_NAME}
                    value={o.name}
                    onClick={(ev) => ev.stopPropagation()}
                    onChange={(ev) => aendereOrt(o.id, { name: ev.target.value })}
                    aria-label={`Name von Ort ${o.buchstabe}`}
                    className="w-full px-2 py-1 text-xs rounded bg-secondary-950 border border-secondary-700 text-white focus:outline-none focus:border-[#2563EB]"
                  />
                  <div
                    className="mt-1 flex items-center gap-1"
                    onClick={(ev) => ev.stopPropagation()}
                  >
                    <span className="text-[10px] text-gray-500">
                      {hatPosition(o) ? "Pin-Größe:" : "Noch nicht gesetzt – auf den Plan klicken"}
                    </span>
                    {hatPosition(o) &&
                      Object.keys(PIN_GROESSEN).map((groesse) => (
                        <button
                          key={groesse}
                          onClick={() => aendereOrt(o.id, { map_size: groesse })}
                          className={`px-1.5 py-0.5 rounded text-[10px] capitalize ${
                            (o.map_size || PIN_GROESSE_STANDARD) === groesse
                              ? "bg-[#2563EB] text-white font-semibold"
                              : "bg-secondary-800 text-gray-400 hover:bg-secondary-700"
                          }`}
                        >
                          {groesse}
                        </button>
                      ))}
                  </div>
                </div>
                <button
                  onClick={(ev) => {
                    ev.stopPropagation();
                    ortEntfernen(o.id);
                  }}
                  className="p-1 rounded hover:bg-secondary-700 text-gray-400"
                  aria-label={`Ort ${o.buchstabe} (${o.name}) löschen`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}

          <form onSubmit={ortHinzufuegen} className="flex gap-2 mt-2">
            <input
              type="text"
              maxLength={MAX_ORT_NAME}
              value={neuerOrt}
              onChange={(ev) => setNeuerOrt(ev.target.value)}
              disabled={!orteGeladen || orte.length >= MAX_ORTE}
              placeholder="z. B. Anmeldung"
              aria-label="Name des neuen Ortes"
              className="flex-1 min-w-0 px-2 py-1 text-xs rounded bg-secondary-950 border border-secondary-700 text-white placeholder-gray-600 focus:outline-none focus:border-[#2563EB] disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={!neuerOrt.trim() || !orteGeladen || orte.length >= MAX_ORTE}
              className="px-2 py-1 rounded bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-50"
            >
              <Plus size={14} />
              Hinzufügen
            </button>
          </form>
        </div>
        </div>

        {/* === Plan === */}
        <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-3">
          {planFehlt ? (
            <div className="border border-dashed border-secondary-700 rounded p-8 text-center text-sm text-gray-400">
              <p className="mb-2">Es ist noch kein Lageplan hinterlegt.</p>
              <p className="text-xs text-gray-500">
                Bild als <code>functions/pdf/assets/lageplan.jpg</code> ablegen (JPEG
                oder PNG) und die Functions neu ausrollen. Danach lässt sich hier
                jede Station auf den Plan setzen.
              </p>
            </div>
          ) : (
            <div className="relative inline-block max-w-full">
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
              <img
                ref={bildRef}
                src={`${BACKEND_URL}/public/lageplan`}
                alt="Lageplan des Festgeländes"
                onClick={setzePin}
                onError={() => setPlanFehlt(true)}
                className="max-w-full h-auto rounded cursor-crosshair select-none"
                draggable={false}
              />

              {stationen.filter(hatPosition).map((s) => {
                const { kreis, text } = pinGroesse(s);
                return (
                  <span
                    key={s.station_number}
                    onClick={(ev) => {
                      // Ein Klick auf einen bestehenden Pin waehlt diese Station
                      // aus, statt die aktuell aktive dorthin zu verschieben –
                      // sonst waere ein Pin nie ohne Umweg korrigierbar.
                      ev.stopPropagation();
                      setAktiv(s.station_number);
                    }}
                    style={{ left: `${s.map_x}%`, top: `${s.map_y}%` }}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 ${kreis} rounded-full flex items-center justify-center ${text} font-bold cursor-pointer border ${
                      s.station_number === aktiv
                        ? "bg-white text-black border-primary-500 ring-2 ring-primary-500"
                        : "bg-primary-500 text-black border-black/70"
                    }`}
                  >
                    {s.station_number}
                  </span>
                );
              })}

              {/* Eigene Orte: dunkle Quadrate mit Buchstaben, wie im Druck. */}
              {orteMitBuchstabe.filter(hatPosition).map((o) => {
                const { kreis, text } = pinGroesse(o);
                return (
                  <span
                    key={o.id}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      waehleOrt(o.id);
                    }}
                    title={o.name}
                    style={{ left: `${o.map_x}%`, top: `${o.map_y}%` }}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 ${kreis} rounded-md flex items-center justify-center ${text} font-bold cursor-pointer border-2 ${
                      o.id === aktivOrt
                        ? "bg-white text-[#1D4ED8] border-[#2563EB] ring-2 ring-[#2563EB]"
                        : "bg-[#2563EB] text-white border-white"
                    }`}
                  >
                    {o.buchstabe}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
